/**
 * Missing Submissions Report Component
 * Shows which learners are enrolled but haven't submitted an assignment
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Alert, CircularProgress, Accordion,
  AccordionSummary, AccordionDetails, List, ListItem, ListItemText,
  Chip, Stack, Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import axios from 'axios';

interface MissingLearner {
  id: number;
  full_name: string;
  student_id: string;
  email: string;
}

interface ClassMissing {
  class_id: number;
  class_name: string;
  total_enrolled: number;
  submitted_count: number;
  missing_count: number;
  missing_learners: MissingLearner[];
}

interface MissingSubmissionsData {
  assignment_id: number;
  assignment_title: string;
  total_enrolled: number;
  total_submitted: number;
  total_missing: number;
  submission_rate: number;
  classes: ClassMissing[];
}

interface Props {
  assignmentId: number;
  collapsed?: boolean;
}

const MissingSubmissionsReport: React.FC<Props> = ({ assignmentId, collapsed = true }) => {
  const [data, setData] = useState<MissingSubmissionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMissingSubmissions = async () => {
      if (!assignmentId) return;

      try {
        setLoading(true);
        setError(null);
        const response = await axios.get<MissingSubmissionsData>(
          `/api/assignments/${assignmentId}/missing_submissions/`
        );
        setData(response.data);
      } catch (err: any) {
        console.error('Error loading missing submissions:', err);
        setError(err.response?.data?.error || 'Failed to load missing submissions report');
      } finally {
        setLoading(false);
      }
    };

    fetchMissingSubmissions();
  }, [assignmentId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  const submissionRateColor =
    data.submission_rate >= 90 ? 'success' :
    data.submission_rate >= 70 ? 'warning' : 'error';

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        {data.total_missing === 0 ? (
          <CheckCircleIcon color="success" fontSize="large" />
        ) : (
          <WarningIcon color={submissionRateColor} fontSize="large" />
        )}
        <Box>
          <Typography variant="h6">
            Submission Status
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {data.total_submitted} of {data.total_enrolled} learners have submitted ({data.submission_rate}%)
          </Typography>
        </Box>
      </Stack>

      {data.total_missing > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={3} mb={2}>
            <Box>
              <Typography variant="body2" color="textSecondary">Total Enrolled</Typography>
              <Typography variant="h6">{data.total_enrolled}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">Submitted</Typography>
              <Typography variant="h6" color="success.main">{data.total_submitted}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">Missing</Typography>
              <Typography variant="h6" color="error.main">{data.total_missing}</Typography>
            </Box>
          </Stack>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
            Missing Submissions by Class:
          </Typography>

          {data.classes.map((classData) => (
            classData.missing_count > 0 && (
              <Accordion key={classData.class_id} defaultExpanded={!collapsed}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                    <PersonOffIcon color="error" />
                    <Box flex={1}>
                      <Typography>{classData.class_name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {classData.submitted_count}/{classData.total_enrolled} submitted
                      </Typography>
                    </Box>
                    <Chip
                      label={`${classData.missing_count} missing`}
                      color="error"
                      size="small"
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {classData.missing_learners.map((learner) => (
                      <ListItem key={learner.id}>
                        <ListItemText
                          primary={learner.full_name}
                          secondary={`${learner.student_id} • ${learner.email}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )
          ))}
        </>
      )}

      {data.total_missing === 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          🎉 Excellent! All enrolled learners have submitted their work.
        </Alert>
      )}
    </Paper>
  );
};

export default MissingSubmissionsReport;
